import React, {useState, useEffect} from 'react';
import {AlertCircle, Clock, User, MessageSquare, Eye} from 'lucide-react';
import dayjs from 'dayjs';

import {useGetReviewQueue} from '../net/query/ReviewQueue.gql';
import {useCompleteReview} from '../net/query/CompleteReview.gql';
import {
  ReviewQueueReason,
  type GetReviewQueueQuery,
} from '../net/types/graphql';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {Badge} from '../components/ui/badge';
import {Button} from '../components/ui/button';
import {Skeleton} from '../components/ui/skeleton';
import {Alert, AlertDescription} from '../components/ui/alert';
import {useToast} from '../components/ui/toast';
import {useSafeNavigate as useNavigate} from '../util/safeNavigate';

/**
 * Component to display a single review queue entry.
 */
const ReviewQueueEntry = ({
  entry,
  onReviewComplete,
  isSelected = false,
}: {
  entry: GetReviewQueueQuery['reviewQueue']['entries'][number];
  onReviewComplete: () => void;
  isSelected?: boolean;
}) => {
  const {showToast} = useToast();
  const navigate = useNavigate();
  const [completeReview, {loading: completingReview}] = useCompleteReview();

  const formatTimestamp = (timestamp: string) => {
    return dayjs(timestamp).format('MMM D, YYYY [at] h:mm A');
  };

  const getReasonColor = (reason: ReviewQueueReason) => {
    switch (reason) {
      case ReviewQueueReason.CourtDateSoon:
        return 'destructive';
      case ReviewQueueReason.Manual:
        return 'secondary';
      case ReviewQueueReason.MsgIn:
        return 'default';
      case ReviewQueueReason.MsgOutAuto:
        return 'outline';
      case ReviewQueueReason.Other:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getReasonLabel = (reason: ReviewQueueReason) => {
    switch (reason) {
      case ReviewQueueReason.CourtDateSoon:
        return 'Court Date Soon';
      case ReviewQueueReason.Manual:
        return 'Manual Review';
      case ReviewQueueReason.MsgIn:
        return 'Message Received';
      case ReviewQueueReason.MsgOutAuto:
        return 'Auto Message Sent';
      case ReviewQueueReason.Other:
        return 'Other';
      default:
        return 'Unknown';
    }
  };

  const handleViewChat = () => {
    // Format the query string.
    // The `r` query parameter is the review queue entry ID.
    // The `m` query parameter is the message ID.
    // If the message ID is not set, we don't include it in the query string.
    // The `r` lets us show the review controls in the sidebar of the chat, and return
    // to the correct position in the review queue.
    const query = [
      ['r', entry.id],
      ['m', entry.lastMessage?.id],
    ]
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${v}`)
      .join('&');

    const url = `/chat/${entry.client.id}?${query}`;
    navigate(url);
  };

  const handleMarkReviewed = async () => {
    try {
      await completeReview({
        variables: {
          reviewQueueEntryId: entry.id,
        },
      });

      showToast('Review marked as complete', 'success');
      onReviewComplete();
    } catch (error) {
      showToast('Failed to mark review as complete', 'error');
      console.error('Error completing review:', error);
    }
  };

  return (
    <Card
      className={`mb-4 hover:shadow-md transition-shadow ${
        isSelected ? 'ring-2 ring-primary ring-offset-2' : ''
      }`}
      id={`review-entry-${entry.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-lg">{entry.client.fullName}</CardTitle>
              <CardDescription>
                Flagged for review on {formatTimestamp(entry.markedForReviewTs)}
              </CardDescription>
            </div>
          </div>
          <Badge variant={getReasonColor(entry.markedForReviewReason)}>
            {getReasonLabel(entry.markedForReviewReason)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Clock className="h-4 w-4" />
              <span>
                Last reviewed:{' '}
                <span className="text-(--constructive)">
                  {entry.lastReviewTs
                    ? formatTimestamp(entry.lastReviewTs)
                    : 'Never'}
                </span>
              </span>
            </div>
            {entry.lastReviewer && (
              <div className="flex items-center space-x-2">
                <span>by</span>
                <User className="h-4 w-4 text-(--constructive)" />
                <span className="text-(--constructive)">
                  {entry.lastReviewer.fullName}
                </span>
              </div>
            )}
          </div>

          {entry.lastMessage && (
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <MessageSquare className="h-4 w-4" />
              <span className="truncate">
                Message:{' '}
                {entry.lastMessage ? (
                  <span className="text-sm">{entry.lastMessage?.content}</span>
                ) : (
                  <em className="text-muted-foreground">
                    No preview available
                  </em>
                )}
              </span>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <Button variant="outline" size="sm" onClick={handleViewChat}>
              <Eye className="h-4 w-4 mr-2" />
              View Chat
            </Button>
            <Button
              size="sm"
              onClick={handleMarkReviewed}
              disabled={completingReview}
            >
              {completingReview ? 'Marking...' : 'Mark Reviewed'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Loading skeleton for review queue entries.
 */
const ReviewQueueSkeleton = () => (
  <div className="space-y-4">
    {Array.from({length: 3}).map((_, i) => (
      <Card key={i} className="mb-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Skeleton className="h-5 w-5" />
              <div>
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <Skeleton className="h-6 w-24" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

/**
 * Main review queue component.
 */
const ReviewQueue = () => {
  const [page, setPage] = useState(0);
  const limit = 10;
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);

  const {data, loading, error, fetchMore} = useGetReviewQueue({
    variables: {
      limit,
      offset: page * limit,
    },
  });

  // Handle the 'r' query parameter for selected entry
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reviewEntryId = urlParams.get('r');

    if (reviewEntryId) {
      setSelectedEntryId(reviewEntryId);

      // Scroll to the selected entry after data loads
      if (data?.reviewQueue.entries) {
        const selectedEntry = data.reviewQueue.entries.find(
          (entry) => entry.id === reviewEntryId,
        );

        if (selectedEntry) {
          setTimeout(() => {
            const element = document.getElementById(
              `review-entry-${reviewEntryId}`,
            );
            if (element) {
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 100); // Small delay to ensure DOM is ready
        }
      }
    }
  }, [data?.reviewQueue.entries]);

  const handleLoadMore = () => {
    if (data?.reviewQueue.hasNextPage) {
      fetchMore({
        variables: {
          limit,
          offset: (page + 1) * limit,
        },
      });
      setPage(page + 1);
    }
  };

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Error loading review queue: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Review Queue</h2>
          <p className="text-muted-foreground">
            {data?.reviewQueue.totalCount || 0} threads flagged for review
          </p>
        </div>
      </div>

      {loading && page === 0 ? (
        <ReviewQueueSkeleton />
      ) : (
        <div className="space-y-4">
          {data?.reviewQueue.entries.map((entry) => (
            <ReviewQueueEntry
              key={entry.id}
              entry={entry}
              isSelected={selectedEntryId === entry.id}
              onReviewComplete={() => {
                // Refetch the review queue to update the list
                fetchMore({
                  variables: {
                    limit,
                    offset: 0, // Reset to first page
                  },
                });
                setPage(0);
              }}
            />
          ))}

          {data?.reviewQueue.entries.length === 0 && !loading && (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No reviews needed</p>
                  <p className="text-sm">
                    All conversation threads are up to date.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {data?.reviewQueue.hasNextPage && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Layout wrapper for the review routes.
 */
export const ReviewWindow = () => {
  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <ReviewQueue />
    </div>
  );
};
